/**
 * The dashboard's feature usage table (SF-5095): for each feature, how many customer companies
 * used it in the month, how many have it switched on, and how many have it on but did not use
 * it. Eva closes the list, counted from its module switch (SF-5117). A dash means the feature
 * has no switch to count.
 */
import { Table } from 'antd';
import type { FeatureUsage, UsageTypeKey } from '../../../components/usage/usageClient';
import { USAGE_TYPE_LABELS } from '../../../components/usage/usageNumbers';
import { formatNumber } from '../../../utils/format';

interface FeatureRow {
  key: UsageTypeKey | 'eva';
  label: string;
  using: number;
  switchedOn: number | null;
  notUsed: number | null;
}

function featureRows(data: FeatureUsage): FeatureRow[] {
  const rows: FeatureRow[] = (data.features ?? []).map((f) => ({
    key: f.type,
    label: USAGE_TYPE_LABELS[f.type] ?? f.type,
    using: f.companies_using,
    switchedOn: f.companies_switched_on,
    notUsed: f.switched_on_not_used,
  }));
  rows.push({
    key: 'eva',
    label: 'Eva',
    using: data.eva?.companies_using ?? 0,
    // The company's Eva module switch (SF-5117); an older API sends neither, shown as a dash.
    switchedOn: data.eva?.switched_on ?? null,
    notUsed: data.eva?.switched_on_not_used ?? null,
  });
  return rows;
}

const right = { align: 'right' as const };

export const FeatureUsageTable: React.FC<{ data: FeatureUsage }> = ({ data }) => (
  <Table<FeatureRow>
    size="small"
    rowKey="key"
    pagination={false}
    dataSource={featureRows(data)}
    columns={[
      { title: 'Feature', dataIndex: 'label', key: 'label' },
      {
        title: 'Companies using',
        key: 'using',
        ...right,
        render: (_, row) => formatNumber(row.using),
      },
      {
        title: 'Switched on',
        key: 'switchedOn',
        ...right,
        render: (_, row) => formatNumber(row.switchedOn),
      },
      {
        title: 'Switched on but not used',
        key: 'notUsed',
        ...right,
        render: (_, row) => formatNumber(row.notUsed),
      },
    ]}
  />
);
